import { useEffect, useRef, useState } from 'react'
import { useTerminalStore } from '../../store/terminalStore'
import { api } from '../../lib/api'

const POPULAR_TICKERS = [
  'AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA',
  'TSLA', 'META', 'JPM', 'V', 'SPY',
  'QQQ', 'NFLX', 'AMD', 'COIN', 'PLTR',
]

const STEPS = [
  { id: 'welcome',  label: 'Welcome' },
  { id: 'watchlist', label: 'Your Watchlist' },
  { id: 'tour',     label: 'Quick Tour' },
]

export function OnboardingModal() {
  const { setOnboardingDone, setSymbol } = useTerminalStore()
  const [step, setStep]           = useState(0)
  const [selected, setSelected]   = useState<string[]>(['AAPL', 'TSLA', 'SPY'])
  const [custom, setCustom]       = useState('')
  const [saving, setSaving]       = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)

  // Focus trap — keep keyboard focus inside the dialog
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !dialogRef.current) return
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
      if (focusables.length === 0) return
      const first = focusables[0]
      const last  = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus()
      }
    }
    window.addEventListener('keydown', handler)
    // Move initial focus into the dialog
    requestAnimationFrame(() => {
      const first = dialogRef.current?.querySelector<HTMLElement>('button, [href], input')
      first?.focus()
    })
    return () => window.removeEventListener('keydown', handler)
  }, [step])

  const toggle = (ticker: string) => {
    setSelected((prev) =>
      prev.includes(ticker) ? prev.filter((t) => t !== ticker) : [...prev, ticker],
    )
  }

  const addCustom = () => {
    const sym = custom.trim().toUpperCase().replace(/[^A-Z0-9.\-^]/g, '')
    if (sym && !selected.includes(sym)) setSelected((p) => [...p, sym])
    setCustom('')
  }

  const finish = async () => {
    setSaving(true)
    try {
      // Add selected tickers to watchlist
      await Promise.allSettled(
        selected.map((sym) =>
          api.post('/watchlist', { symbol: sym }).catch(() => null),
        ),
      )
      // Set first selected as active symbol
      if (selected.length > 0) setSymbol(selected[0])
    } finally {
      setSaving(false)
      setOnboardingDone()
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bb-onboarding-title"
        className="bb-panel w-full max-w-lg mx-4 overflow-hidden"
      >
        {/* Progress */}
        <div className="flex border-b border-bb-border" role="progressbar" aria-valuemin={1} aria-valuemax={STEPS.length} aria-valuenow={step + 1} aria-label={`Step ${step + 1} of ${STEPS.length}: ${STEPS[step].label}`}>
          {STEPS.map((s, i) => (
            <div
              key={s.id}
              aria-hidden="true"
              className={`flex-1 py-2 text-center text-2xs transition-colors ${
                i === step
                  ? 'bg-bb-orange/10 text-bb-orange border-b-2 border-bb-orange'
                  : i < step
                  ? 'text-bb-green'
                  : 'text-bb-disabled'
              }`}
            >
              {i < step ? '✓ ' : `${i + 1}. `}{s.label}
            </div>
          ))}
        </div>

        {/* Step 0: Welcome */}
        {step === 0 && (
          <div className="p-8 text-center space-y-4">
            <div className="text-4xl" aria-hidden="true">📊</div>
            <h2 id="bb-onboarding-title" className="text-lg font-bold text-bb-orange">Welcome to QuantTradeAI</h2>
            <p className="text-bb-subtle text-sm leading-relaxed">
              Your AI-powered trading terminal. Real-time data, AI analysis,
              and professional-grade tools — all in your browser.
            </p>
            <div className="grid grid-cols-2 gap-3 pt-2 text-left">
              {[
                ['📈', 'Live charts + technicals'],
                ['🤖', 'AI analysis (Claude, GPT-4o)'],
                ['📰', 'News & earnings tracking'],
                ['💼', 'Portfolio analytics'],
              ].map(([icon, text]) => (
                <div key={text} className="flex items-center gap-2 text-xs text-bb-subtle">
                  <span className="text-base">{icon}</span>
                  <span>{text}</span>
                </div>
              ))}
            </div>
            <button className="bb-btn-primary w-full mt-4" onClick={() => setStep(1)}>
              Get Started →
            </button>
          </div>
        )}

        {/* Step 1: Watchlist */}
        {step === 1 && (
          <div className="p-6 space-y-4">
            <div>
              <h2 id="bb-onboarding-title" className="text-sm font-semibold text-bb-text">Build Your Watchlist</h2>
              <p className="text-2xs text-bb-subtle mt-1">Select stocks you want to track. You can edit this anytime.</p>
            </div>

            <div className="flex flex-wrap gap-2" role="group" aria-label="Select tickers to add to your watchlist">
              {POPULAR_TICKERS.map((t) => (
                <button
                  key={t}
                  onClick={() => toggle(t)}
                  aria-pressed={selected.includes(t)}
                  className={`px-2 py-1 text-2xs rounded border transition-colors ${
                    selected.includes(t)
                      ? 'bg-bb-orange/20 text-bb-orange border-bb-orange/50'
                      : 'text-bb-subtle border-bb-border hover:border-bb-text hover:text-bb-text'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                className="bb-input flex-1 text-xs"
                placeholder="Add custom symbol (e.g. SHOP)"
                value={custom}
                onChange={(e) => setCustom(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && addCustom()}
              />
              <button className="bb-btn-ghost text-xs shrink-0" onClick={addCustom}>Add</button>
            </div>

            {selected.length > 0 && (
              <p className="text-2xs text-bb-green">
                {selected.length} ticker{selected.length !== 1 ? 's' : ''} selected: {selected.join(', ')}
              </p>
            )}

            <div className="flex gap-2 pt-2">
              <button className="bb-btn-ghost text-xs flex-1" onClick={() => setStep(0)}>← Back</button>
              <button className="bb-btn-primary text-xs flex-1" onClick={() => setStep(2)}>Next →</button>
            </div>
          </div>
        )}

        {/* Step 2: Quick tour */}
        {step === 2 && (
          <div className="p-6 space-y-4">
            <h2 id="bb-onboarding-title" className="text-sm font-semibold text-bb-text">Quick Tour</h2>
            <div className="space-y-3">
              {[
                { key: 'terminal', icon: '📊', title: 'Terminal', desc: 'Main dashboard — charts, quotes, and right-side panels. Drag to reorder.' },
                { key: 'watchlist', icon: '⭐', title: 'Watchlist', desc: 'Your saved tickers with live prices and change percentages.' },
                { key: 'ai', icon: '🤖', title: 'AI Analysis', desc: 'Inside the terminal, click ANALYZE to get AI insights on any stock.' },
                { key: 'settings', icon: '⚙️', title: 'Settings', desc: 'Add your own API keys (Anthropic, OpenAI, Gemini) for unlimited AI calls.' },
              ].map((item) => (
                <div key={item.key} className="flex items-start gap-3 p-2 rounded border border-bb-border/50 bg-bb-surface/30">
                  <span className="text-xl shrink-0">{item.icon}</span>
                  <div>
                    <div className="text-xs font-semibold text-bb-text">{item.title}</div>
                    <p className="text-2xs text-bb-subtle leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 pt-2">
              <button className="bb-btn-ghost text-xs flex-1" onClick={() => setStep(1)}>← Back</button>
              <button
                className="bb-btn-primary text-xs flex-1"
                onClick={finish}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Go to Terminal →'}
              </button>
            </div>

            <p className="text-2xs text-bb-disabled text-center">
              Not financial advice.{' '}
              <button className="underline hover:text-bb-subtle" onClick={finish}>Skip</button>
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
